import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { taskAPI, systemAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';
import { 
  DocumentDuplicateIcon,
  ClockIcon,
  CheckCircleIcon,
  PlusIcon,
  SparklesIcon,
  MapPinIcon,
  HomeIcon,
  AcademicCapIcon,
  CreditCardIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import RELOCATION_WORKFLOWS from '../../data/relocationWorkflows';

const RelocationWorkflowTemplates = () => {
  const navigate = useNavigate();
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const workflowIcons = {
    NIE_APPLICATION: DocumentDuplicateIcon,
    'nie-application': DocumentDuplicateIcon,
    TIE_CARD: CreditCardIcon,
    'tie-card': CreditCardIcon,
    EMPADRONAMIENTO: MapPinIcon,
    'empadronamiento': MapPinIcon,
    HOUSING_SEARCH: HomeIcon,
    'housing-search': HomeIcon,
    SCHOOL_ENROLLMENT: AcademicCapIcon,
    'school-enrollment': AcademicCapIcon
  };

  const workflowColors = {
    NIE_APPLICATION: 'bg-blue-500',
    'nie-application': 'bg-blue-500',
    TIE_CARD: 'bg-green-500',
    'tie-card': 'bg-green-500',
    EMPADRONAMIENTO: 'bg-purple-500',
    'empadronamiento': 'bg-purple-500',
    HOUSING_SEARCH: 'bg-orange-500',
    'housing-search': 'bg-orange-500',
    SCHOOL_ENROLLMENT: 'bg-pink-500',
    'school-enrollment': 'bg-pink-500'
  };

  useEffect(() => {
    let isMounted = true;
    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch task templates (step-by-step processes), not automation workflows
        const response = await systemAPI.getTemplates({ type: 'task' });
        if (isMounted && response.data?.success) {
          setTemplates(response.data.data || []);
        }
      } catch (err) {
        console.error('Failed to load shared workflow templates:', err);
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTemplates();
    return () => {
      isMounted = false;
    };
  }, []);

  const computedTemplates = useMemo(() => {
    if (templates.length > 0) {
      return templates.map(template => ({
        key: template.slug || template.id,
        id: template.id,
        title: template.title,
        description: template.description,
        category: template.category || 'relocation',
        estimatedDuration: template.content?.estimatedDuration || '—',
        steps: template.content?.steps || [],
        isNew: !!template.isNew,
        highlightLabel: template.highlightLabel || 'New',
        source: 'remote',
        raw: template
      }));
    }

    return Object.entries(RELOCATION_WORKFLOWS).map(([key, workflow]) => ({
      key,
      id: key,
      title: workflow.title,
      description: workflow.description,
      category: workflow.category,
      estimatedDuration: workflow.estimatedDuration,
      steps: workflow.steps || [],
      isNew: false,
      highlightLabel: 'Template',
      source: 'static',
      raw: workflow
    }));
  }, [templates]);

  const handleCreateFromTemplate = async (workflowKey) => {
    const template = computedTemplates.find(item => item.key === workflowKey);
    const workflow = template?.source === 'remote'
      ? template.raw?.content
      : RELOCATION_WORKFLOWS[workflowKey] || template?.raw;
    
    try {
      setCreating(true);
      
      // Transform workflow data to match task template format
      const templateData = {
        title: template?.title || workflow?.title,
        description: template?.description || workflow?.description,
        category: template?.category || workflow?.category || 'relocation',
        steps: (workflow?.steps || []).map((step, index) => ({
          title: step.title,
          description: step.description,
          order: step.order ?? index,
          estimatedDuration: step.estimatedDuration || '',
          requiredDocuments: step.requiredDocuments || [],
          links: step.links || []
        })),
        isActive: true,
        tags: [template?.category || workflow?.category || 'relocation', 'relocation', 'spain'].filter(Boolean)
      };

      const response = await taskAPI.createTemplate(templateData);
      
      if (response.data && response.data.success) {
        toast.success(`Task template "${template?.title || workflow?.title}" created successfully!`);
        navigate('/task-templates');
      } else {
        toast.error(response.data?.message || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 dark:from-blue-950/10 to-purple-50 dark:to-purple-950/10 border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Relocation Workflow Templates</h2>
              <p className="text-muted-foreground">Pre-built workflows for Spanish relocation processes</p>
            </div>
          </div>
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            {computedTemplates.length} Templates
          </Badge>
        </div>
      </Card>

      {error && (
        <Card className="p-4 border border-red-200 bg-red-50 text-red-700">
          Failed to sync shared templates. Showing default library.
        </Card>
      )}

      {/* Workflow Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && templates.length === 0 && (
          <Card className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-muted rounded w-1/2"></div>
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
              <div className="flex space-x-2">
                <div className="h-10 bg-muted rounded w-1/2"></div>
                <div className="h-10 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          </Card>
        )}
        {computedTemplates.map(template => {
          const Icon = workflowIcons[template.key] || SparklesIcon;
          const colorClass = workflowColors[template.key] || 'bg-blue-500';
          
          return (
            <Card key={template.key} className="p-6 hover:shadow-lg transition-shadow relative">
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${colorClass}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <Badge variant="outline" className="text-xs">
                  {template.category}
                </Badge>
              </div>
              
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {template.title}
              </h3>
              
              <p className="text-muted-foreground text-sm mb-4">
                {template.description}
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-muted-foreground">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Duration: {template.estimatedDuration}
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  {template.steps.length} steps
                </div>
              </div>
              
              {template.isNew && (
                <Badge className="absolute top-4 right-4 bg-green-500 text-white">
                  {template.highlightLabel || 'New'}
                </Badge>
              )}

              <div className="space-y-2">
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleCreateFromTemplate(template.key)}
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Create Template
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setSelectedWorkflow(template.key)}
                >
                  View Details
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Workflow Details Modal */}
      {selectedWorkflow && (() => {
        const template = computedTemplates.find(item => item.key === selectedWorkflow);
        if (!template) return null;
        const workflow = template.source === 'remote'
          ? template.raw?.content
          : RELOCATION_WORKFLOWS[selectedWorkflow] || template.raw;

        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto scrollbar-modal">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  {(() => {
                    const Icon = workflowIcons[selectedWorkflow] || SparklesIcon;
                    const colorClass = workflowColors[selectedWorkflow] || 'bg-blue-500';
                    return (
                      <>
                        <div className={`p-3 rounded-lg ${colorClass}`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-foreground">
                            {template.title}
                          </h3>
                          <p className="text-muted-foreground">
                            {template.description}
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedWorkflow(null)}
                >
                  Close
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    Duration: {workflow?.estimatedDuration || '—'}
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    {(workflow?.steps || []).length} steps
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">Process Steps:</h4>
                  {(workflow?.steps || []).map((step, index) => (
                    <div key={index} className="p-4 bg-muted rounded-lg">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                          {step.order ?? index + 1}
                        </div>
                        <div className="flex-1">
                          <h5 className="font-medium text-foreground mb-1">{step.title}</h5>
                          <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                          <div className="text-xs text-muted-foreground mb-2">
                            Duration: {step.estimatedDuration}
                          </div>
                          {step.requiredDocuments && step.requiredDocuments.length > 0 && (
                            <div className="mb-2">
                              <div className="text-xs font-medium text-foreground mb-1">Required Documents:</div>
                              <ul className="text-xs text-muted-foreground list-disc list-inside">
                                {step.requiredDocuments.map((doc, docIndex) => (
                                  <li key={docIndex}>{doc}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {step.links && step.links.length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-foreground mb-1">Useful Links:</div>
                              <div className="space-y-1">
                                {step.links.map((link, linkIndex) => (
                                  <a 
                                    key={linkIndex}
                                    href={link.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:text-blue-800 block"
                                  >
                                    {link.title}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      handleCreateFromTemplate(selectedWorkflow);
                      setSelectedWorkflow(null);
                    }}
                    disabled={creating}
                  >
                    {creating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Create Template
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setSelectedWorkflow(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
        );
      })()}
    </div>
  );
};

export default RelocationWorkflowTemplates;
